import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Radio Button API",
  "title": "Radio Button",
  "props": ["RadioProps", "RadioGroupProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const Alert = makeShortcode("Alert");
const RadioGroupProps = makeShortcode("RadioGroupProps");
const RadioProps = makeShortcode("RadioProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="radio_button" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Radio buttons are form elements used to select one item from a set of mutually
  exclusive options.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`RadioGroup`}</inlineCode>{` component is used as the container for the set of options. The `}<inlineCode parentName="p">{`Radio`}</inlineCode>{` component is used for each of the options themselves.`}</p>
      <p>{`See also: `}<Link to="/api/checkboxes" mdxType="Link">{`Checkboxes`}</Link>{` and `}<Link to="/api/toggle" mdxType="Link">{`Toggle Switches`}</Link>{`.`}</p>
      <p>{`The label/radio association will be handled via the passed in `}<inlineCode parentName="p">{`id`}</inlineCode>{` if one is supplied or via an auto-generated `}<inlineCode parentName="p">{`id`}</inlineCode>{` if not supplied.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup labelText="Basic usage" id="basicGroup" name="basic">
      <Radio id="radio1" labelText="Option one label" value="1" />

      <Radio
        id="radio2"
        labelText="Option two label is really long and can wrap to multiple lines lorem ipsum dolar sit amet is really long and can wrap to multiple lines"
        value="2"
      />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Default Selected Value`}</h2>
      <p>{`To set a selected value by default, use the `}<inlineCode parentName="p">{`value`}</inlineCode>{` prop on the `}<inlineCode parentName="p">{`RadioGroup`}</inlineCode>{` to pass in the `}<inlineCode parentName="p">{`value`}</inlineCode>{` of the option you wish to select.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup
      labelText="Default selected value"
      id="defaultSelectedGroup"
      name="defaultSelected"
      value="selectedOption"
    >
      <Radio
        id="radioSelected"
        labelText="Selected option"
        value="selectedOption"
      />

      <Radio
        id="radioUnselected"
        labelText="Unselected option"
        value="unselectedOption"
      />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Colors`}</h2>
      <p>{`By default, the radio buttons use the theme's primary color. But any other color may be passed in.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio, magma } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup labelText="Colors" id="colorsGroup" name="colors">
      <Radio id="colorRadio" labelText="Default color" value="default" />
      <Radio
        color={magma.colors.success}
        id="successColorRadio"
        labelText="Success color"
        value="success"
      />
      <Radio
        color={magma.colors.danger}
        id="dangerColorRadio"
        labelText="Danger color"
        value="danger"
      />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Disabled`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup
      labelText="Disabled radio buttons"
      id="disabledGroup"
      name="disabled"
      value="selectedOption"
    >
      <Radio
        disabled
        id="disabledRadio"
        labelText="Disabled radio 1"
        value="1"
      />
      <Radio
        disabled
        id="disabledRadio2"
        labelText="Disabled radio 2"
        value="2"
      />
      <Radio
        disabled
        id="disabledRadio3"
        labelText="Disabled radio 3 selected"
        value="selectedOption"
      />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Helper Message`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`helperMessage`}</inlineCode>{` appears underneath the group of radio buttons. It will not appear if an error message is present. Can be a node or a string.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup
      helperMessage="Your choice of color is important"
      labelText="Choose a color"
      name="helperMessage"
    >
      <Radio labelText="Red" value="R" />
      <Radio labelText="Blue" value="B" />
      <Radio labelText="Yellow" value="Y" />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Required Fields / Error Message`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`required`}</inlineCode>{` attribute can be indicated to the `}<inlineCode parentName="p">{`RadioGroup`}</inlineCode>{` component if a selection is required.
You will also need to indicate the required field to the user in the UI.`}</p>
      <p>{`If a radio group has an `}<inlineCode parentName="p">{`errorMessage`}</inlineCode>{`, the radio buttons will be styled to highlight the error state and the error message will appear below the fields.
If an error message is present, it will replace the helper text. Can be a node or a string.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  RadioGroup,
  Radio,
  Button,
  ButtonColor,
  ButtonGroup,
} from 'react-magma-dom';

export function Example() {
  const [hasError, setHasError] = React.useState(false);
  const [requiredFieldsValue, setRequiredFieldsValue] = React.useState('');

  function changeValue(e) {
    setRequiredFieldsValue(e.target.value);
  }

  function submit() {
    if (requiredFieldsValue === '') {
      setHasError(true);
    } else {
      setHasError(false);
    }
  }

  function reset() {
    setHasError(false);
    setRequiredFieldsValue('');
  }

  const errorMsg = hasError ? 'Please select a color' : null;

  return (
    <>
      <p>* indicates required fields</p>
      <RadioGroup
        errorMessage={errorMsg}
        required
        labelText="Choose a color *"
        name="requiredFields"
        onChange={changeValue}
        value={requiredFieldsValue}
      >
        <Radio labelText="Red" value="R" />
        <Radio labelText="Blue" value="B" />
        <Radio labelText="Yellow" value="Y" />
      </RadioGroup>
      <br />
      <ButtonGroup>
        <Button onClick={submit}>Submit</Button>
        <Button onClick={reset} color={ButtonColor.secondary}>
          Reset
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`isInverse`}</inlineCode>{` property is an optional boolean can be applied to the containing `}<inlineCode parentName="p">{`RadioGroup`}</inlineCode>{`, that reverses the colors so that the radio buttons can better appear on a dark background.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio, Card, CardBody, magma } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Card isInverse style={{ marginBottom: magma.spaceScale.spacing04 }}>
        <CardBody>
          <RadioGroup
            labelText="Inverse radio buttons"
            id="inverseGroup"
            isInverse
            name="inverse"
            value="selectedOption"
          >
            <Radio id="inverseRadio" labelText="Inverse" value="default" />
            <Radio
              color={magma.colors.success}
              id="successInverseRadio"
              labelText="Success inverse"
              value="success"
            />
            <Radio
              color={magma.colors.danger}
              id="dangerInverseRadio"
              labelText="Danger inverse"
              value="danger"
            />
            <Radio
              disabled
              id="disabledInverseRadio"
              labelText="Disabled inverse"
              value="disabled"
            />
            <Radio
              disabled
              id="disabledInverseRadio"
              labelText="Disabled inverse selected"
              value="selectedOption"
            />
          </RadioGroup>
        </CardBody>
      </Card>

      <Card isInverse>
        <CardBody>
          <p>* indicates required fields</p>
          <RadioGroup
            errorMessage="Please select a color"
            isInverse
            required
            labelText="Inverse with error *"
            name="requiredFieldsInverse"
          >
            <Radio labelText="Option 1" value="R" />
            <Radio labelText="Option2" value="B" />
          </RadioGroup>
        </CardBody>
      </Card>
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Styles`}</h2>
      <p>{`Custom styles can be passed into both the RadioGroup component and the Radio component. The `}<inlineCode parentName="p">{`containerStyle`}</inlineCode>{` property will apply to the container.
Additional `}<inlineCode parentName="p">{`labelStyle`}</inlineCode>{` and `}<inlineCode parentName="p">{`inputStyle`}</inlineCode>{` properties are available to style the respective elements. Please use discretion when adding custom styles.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup
      style={{ border: '2px solid yellow', padding: '10px' }}
      labelStyle={{ color: 'blue', textTransform: 'uppercase' }}
      labelText="Custom styles"
      id="customGroup"
      name="custom"
    >
      <Radio
        id="custom1"
        containerStyle={{ border: '1px dotted purple' }}
        inputStyle={{ boxShadow: '3px 3px 3px rgba(0, 0 , 0, .6)' }}
        labelStyle={{ color: 'brown', fontStyle: 'italic' }}
        labelText="Custom styles"
        value="1"
      />

      <Radio id="custom2" labelText="No custom styles" value="2" />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Node as labelText`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`labelText`}</inlineCode>{` prop can either be a string, or it can be a React Node.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  const labelStyles = {
    border: '1px solid #3f3f3f',
    borderRadius: '3px',
    display: 'inline-block',
    padding: '10px',
  };

  const inputStyles = { alignSelf: 'center' };

  return (
    <RadioGroup labelText="Nodes for labels">
      <Radio
        labelText={<span style={labelStyles}>Option 1</span>}
        inputStyle={inputStyles}
        value="1"
      />

      <Radio
        labelText={<span style={labelStyles}>Option 2</span>}
        inputStyle={inputStyles}
        value="2"
      />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Labeling Radio Groups`}</h2>
      <p>{`To meet accessibility requirements, a radio group `}<em parentName="p">{`must`}</em>{` have a label. You can provide a label in one of two ways.`}</p>
      <p>{`You can use the `}<inlineCode parentName="p">{`labelText`}</inlineCode>{` prop, which takes a string. This string will be displayed on screen inside a `}<inlineCode parentName="p">{`<label>`}</inlineCode>{` element and be associated with your radio group.`}</p>
      <p>{`Or you can use the `}<inlineCode parentName="p">{`labelledById`}</inlineCode>{` prop, which also takes a string. This string should refer to an ID of an element that exists somewhere else on the page.
This id will become the value of the `}<inlineCode parentName="p">{`aria-labelledby`}</inlineCode>{` attribute for the group.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <RadioGroup labelText="Labelled by label" name="labelledByProp">
        <Radio value="1" labelText="Label" />
        <Radio value="2" labelText="Label" />
      </RadioGroup>

      <RadioGroup labelledById="h3Label" name="labelledByChildren">
        <h3 id="h3Label">Labelled by H3</h3>
        <Radio value="3" labelText="Label" />
        <Radio value="4" labelText="Label" />
      </RadioGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Text Position`}</h2>
      <p>{`Use the optional prop `}<inlineCode parentName="p">{`textPosition`}</inlineCode>{` to position the label text on either side of the radio button.
The `}<inlineCode parentName="p">{`textPosition`}</inlineCode>{` property takes the values `}<inlineCode parentName="p">{`left`}</inlineCode>{` and `}<inlineCode parentName="p">{`right`}</inlineCode>{`. The default value is `}<inlineCode parentName="p">{`right`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio, RadioTextPosition } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup labelText="Text position" name="textPosition">
      <Radio
        labelText="Text left"
        textPosition={RadioTextPosition.left}
        value="1"
      />
      <Radio labelText="Text right" value="2" />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Visually Hidden Label`}</h2>
      <p>{`Adding the `}<inlineCode parentName="p">{`isTextVisuallyHidden`}</inlineCode>{` prop will make the label text for either the radio group or the radio button available to screen readers, but will hide it visually.`}</p>
      <Alert variant="warning" mdxType="Alert">
  Use caution when using the <inlineCode>isTextVisuallyHidden</inlineCode> prop;
  it should only be used in rare circumstances when the purpose of the radio
  button is conveyed in another way. While screen readers will announce the
  label when the radio button gains focus, it is still best to have a visual
  label. This is especially helpful for individuals with cognitive disabilities
  and users who have fine motor control limitations.
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { RadioGroup, Radio } from 'react-magma-dom';

export function Example() {
  return (
    <RadioGroup
      labelText="Hidden label"
      id="visuallyHiddenRadioGroup"
      name="visuallyHiddenRadioGroup"
      isTextVisuallyHidden
    >
      <Radio
        value="1"
        id="visuallyHiddenRadio1"
        labelText="Hidden label"
        isTextVisuallyHidden
      />
      <Radio
        value="2"
        id="visuallyHiddenRadio2"
        labelText="Hidden label"
        isTextVisuallyHidden
      />
    </RadioGroup>
  );
}
`}</code></pre>
      <h2>{`Forward Ref`}</h2>
      <p>{`Using React's `}<inlineCode parentName="p">{`forwardRef`}</inlineCode>{` feature you can gain access to the reference of the internal radio.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  RadioGroup,
  Radio,
  Button,
  ButtonColor,
  ButtonGroup,
} from 'react-magma-dom';

export function Example() {
  const radioRef = React.useRef();
  const [radioGroupVal, setRadioGroupVal] = React.useState(null);

  function handleSelectOne() {
    if (!radioRef.current) {
      setRadioGroupVal(null);
    } else if (radioRef.current.checked) {
      setRadioGroupVal('');
    } else if (!radioRef.current.checked) {
      setRadioGroupVal('1');
    }
  }

  function handleFocusOne() {
    radioRef.current.focus();
  }

  return (
    <>
      <RadioGroup
        labelText="Forward ref usage"
        id="forwardRefGroup"
        name="forwardRef"
        value={radioGroupVal}
      >
        <Radio
          ref={radioRef}
          id="forwardRefRadio1"
          labelText="Option one label"
          value="1"
        />

        <Radio id="forwardRefRadio2" labelText="Option two label" value="2" />
      </RadioGroup>

      <ButtonGroup>
        <Button onClick={handleSelectOne}>Toggle option 1 selection</Button>
        <Button color={ButtonColor.secondary} onClick={handleFocusOne}>
          Focus option 1
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Testing`}</h2>
      <p>{`Passing in the `}<inlineCode parentName="p">{`testId`}</inlineCode>{` prop will add the `}<inlineCode parentName="p">{`data-testid`}</inlineCode>{` attribute to the wrapper element for `}<inlineCode parentName="p">{`RadioGroup`}</inlineCode>{` and the input element for `}<inlineCode parentName="p">{`Radio`}</inlineCode>{` for easier querying in tests.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-html",
          "metastring": "noCodeSandbox",
          "noCodeSandbox": true
        }}>{`<RadioGroup
  labelText="Labelled by label"
  name="labelledByProp"
  testId="radiogroup-id"
>
  <Radio value="1" labelText="Label" testId="radio-1" />
  <Radio value="2" labelText="Label" testId="radio-2" />
</RadioGroup>
`}</code></pre>
      <h2>{`Radio Group Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the outer `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes"
        }}>{`global HTML attributes`}</a>{` can be provided as props and will be applied to the wrapping `}<inlineCode parentName="p">{`div`}</inlineCode>{` element that gets rendered.`}</p>
      <RadioGroupProps mdxType="RadioGroupProps" />
      <h2>{`Radio Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the `}<inlineCode parentName="strong">{`input`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input"
        }}>{`standard input attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`input`}</inlineCode>{` element that gets rendered.`}</p>
      <RadioProps mdxType="RadioProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      